import { FC, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Text } from "@mantine/core";
import { FormErrors } from "@funded-here-interface/common/src/Utils/validateForm";
import { useStyle } from "../styles/BorrowerDocUpload";

/**
 * Configuration for mapping error sections to tabs
 */
interface TabConfig {
  index: string;
  route: string;
  label: string;
}

interface ErrorSummaryProps {
  errors: FormErrors;
  setActiveTab: (tab: string) => void;
  /**
   * Optional custom tab mapping
   * If not provided, uses default merchant onboarding tabs
   */
  tabMapping?: Record<string, TabConfig>;
}

/**
 * Default tab mapping for merchant onboarding flow
 * Can be overridden via props for different flows
 */
const DEFAULT_TAB_MAPPING: Record<string, TabConfig> = {
  businessProfile: {
    index: "0",
    route: "business-profile",
    label: "Business Profile",
  },
  representative: {
    index: "1",
    route: "representative",
    label: "Representative",
  },
  bankDetails: {
    index: "2",
    route: "bank-details",
    label: "Bank Details",
  },
  documents: {
    index: "3",
    route: "documents",
    label: "Documents",
  },
};

const ErrorSummary: FC<ErrorSummaryProps> = ({
  errors,
  setActiveTab,
  tabMapping = DEFAULT_TAB_MAPPING,
}) => {
  const { classes } = useStyle();
  const navigate = useNavigate();
  const location = useLocation();

  // Create a reverse lookup map for efficient label retrieval
  const indexToTab = useMemo(() => {
    return Object.values(tabMapping).reduce((acc, tab) => {
      acc[tab.index] = tab;
      return acc;
    }, {} as Record<string, TabConfig>);
  }, [tabMapping]);

  // Group errors by tab index
  const errorGroups = useMemo(() => {
    const groups: Record<string, { field: string; message: string }[]> = {};

    Object.keys(errors).forEach((section) => {
      const tabInfo = tabMapping[section];
      if (!tabInfo) return;

      const sectionErrors = errors[section as keyof FormErrors];
      if (!sectionErrors || typeof sectionErrors !== "object") return;

      groups[tabInfo.index] = Object.entries(sectionErrors).map(
        ([field, message]) => ({
          field: `${section}.${field}`,
          message: message as string,
        })
      );
    });

    return groups;
  }, [errors, tabMapping]);

  /**
   * Handles navigation to the tab containing the error
   */
  const handleErrorClick = (field: string) => {
    const [section] = field.split(".");
    const tab = tabMapping[section];
    if (tab) {
      setActiveTab(tab.index);
      navigate(
        { pathname: location.pathname, hash: tab.route },
        { replace: true }
      );
    }
  };

  return (
    <div className={classes.errorSummary} role="alert" aria-live="polite">
      <Text weight={700}>
        Please complete the following field(s) before submission:{" "}
      </Text>
      {Object.keys(errorGroups).map((tabIndex) => {
        const tabInfo = indexToTab[tabIndex];
        if (!tabInfo) return null;

        return (
          <div key={tabIndex}>
            <Text weight={500}>{tabInfo.label}</Text>
            <ul>
              {errorGroups[tabIndex].map(({ field, message }) => (
                <li key={field}>
                  <button
                    type="button"
                    onClick={() => handleErrorClick(field)}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      color: "inherit",
                      textDecoration: "underline",
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    {message}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default ErrorSummary;
